import { TokenType } from './action';
import { FanoutStackEntry } from './execution';

export enum DataType {
  STRING = 'STRING',
  NUMBER = 'NUMBER',
  DATE = 'DATE',
  BOOLEAN = 'BOOLEAN',
  EMAIL = 'EMAIL',
  OBJECT = 'OBJECT',
  ARRAY = 'ARRAY',
  ANY = 'ANY',
  FILE = 'FILE',
  NON_DECIMAL = 'NON_DECIMAL',
}

export type TokenizedSource<T extends DataType = DataType> = {
  dataType?: T;
  type: 'TOKENIZED';
  parts: TokenizedValue[];
};

type TokenizedValue<T extends DataType = DataType> =
  | ValueSource<T>
  | VariableSource<T>
  | SecretSource
  | ConnectCredentialSource
  | ObjectValueSource
  | PersonaMetadataSource;

type ObjectValueSource<T extends DataType = DataType> = {
  type: 'OBJECT_VALUE';
  name: string;
  path: string[];
  dataType?: T;
};

type VariableSource<T extends DataType = DataType> = {
  dataType?: T;
  type: 'VARIABLE';
  stepId: string;
  path: string[];
};

type SecretSource = {
  type: 'ENVIRONMENT_SECRET';
  environmentSecretId: string;
};

type ValueSource<T extends DataType = DataType> = {
  dataType?: T;
  type: 'VALUE';
  value: DataTypeValues[T];
};

type ConnectCredentialSource =
  | {
      type: 'CONNECT_CREDENTIAL_FIELD';
      fieldType: 'WORKFLOW_SETTING';
      inputId: string;
      /**
       * @since PARA-3065: ConnectInputValue can now include more than a string/number type, so a
       * path is specified similarly to VariableSource to traverse object types.
       */
      path?: string[];
    }
  | {
      type: 'CONNECT_CREDENTIAL_FIELD';
      fieldType: 'SHARED_WORKFLOW_SETTING';
      inputId: string;
      path?: string[];
    }
  | {
      type: 'CONNECT_CREDENTIAL_FIELD';
      fieldType: 'EXTERNAL_USER_ID';
    }
  | {
      type: 'CONNECT_CREDENTIAL_FIELD';
      fieldType: 'EXTERNAL_USER_PROVIDER_ID';
    }
  | {
      type: 'CONNECT_CREDENTIAL_FIELD';
      fieldType: 'OAUTH_ACCESS_TOKEN';
    }
  | {
      type: 'CONNECT_CREDENTIAL_FIELD';
      fieldType: 'EXTERNAL_USER_PROVIDER_DATA';
      dataKey: string;
    };

type PersonaMetadataSource = {
  type: 'PERSONA_METADATA';
  path?: string[];
};

type DataTypeValues = {
  [DataType.STRING]: string;
  [DataType.NUMBER]: number;
  [DataType.DATE]: Date;
  [DataType.BOOLEAN]: boolean;
  [DataType.EMAIL]: string;
  [DataType.OBJECT]: object;
  [DataType.ARRAY]: any[];
  [DataType.ANY]: any;
  [DataType.FILE]: FileValue;
  [DataType.NON_DECIMAL]: number;
};

type FileDataType = Buffer;

type FileValue = {
  data: FileDataType;
  dataType: DataType.FILE;
  encoding?: string;
  id?: string;
  mimeType?: string;
  name?: string;
  size?: string;
};

export type KeyedSource<T extends DataType = DataType> = {
  key: string;
  source: Source<T>;
};

export type Source<T extends DataType = DataType> =
  | ValueSource<T>
  | VariableSource<T>
  | TokenizedSource<T>
  | ConditionSource
  | SecretSource
  | ExecutionSource
  | FanoutExecutionSource
  | UserSuppliedCredentialSource
  | ConnectCredentialSource
  | ObjectValueSource
  | PersonaMetadataSource;

type ConditionSource = {
  type: 'CONDITION';
  condition: ConditionWrapper;
};

type ConditionWrapper = JoinedConditions | OperatorCondition;
type JoinedConditions = AndConditions | OrConditions;
type AndConditions = {
  type: 'JOIN';
  join: 'AND';
  conditions: ConditionWrapper[];
};

export type OrConditions = {
  type: 'JOIN';
  join: 'OR';
  conditions: ConditionWrapper[];
};

type OperatorCondition = {
  type: 'OPERATOR';
  condition: Condition;
};

type Condition =
  | {
      operator: Operator.None;
      variable: Source<DataType.ANY>;
    }
  | {
      operator: Operator.StringContains;
      variable: Source<DataType.STRING>;
      argument: Source<DataType.STRING>;
    }
  | {
      operator: Operator.StringDoesNotContain;
      variable: Source<DataType.STRING>;
      argument: Source<DataType.STRING>;
    }
  | {
      operator: Operator.StringExactlyMatches;
      variable: Source<DataType.STRING>;
      argument: Source<DataType.STRING>;
    }
  | {
      operator: Operator.StringDoesNotExactlyMatch;
      variable: Source<DataType.STRING>;
      argument: Source<DataType.STRING>;
    }
  | {
      operator: Operator.StringIsIn;
      variable: Source<DataType.STRING>;
      argument: Source<DataType.STRING>;
    }
  | {
      operator: Operator.StringIsNotIn;
      variable: Source<DataType.STRING>;
      argument: Source<DataType.STRING>;
    }
  | {
      operator: Operator.StringStartsWith;
      variable: Source<DataType.STRING>;
      argument: Source<DataType.STRING>;
    }
  | {
      operator: Operator.StringDoesNotStartWith;
      variable: Source<DataType.STRING>;
      argument: Source<DataType.STRING>;
    }
  | {
      operator: Operator.StringEndsWith;
      variable: Source<DataType.STRING>;
      argument: Source<DataType.STRING>;
    }
  | {
      operator: Operator.StringDoesNotEndWith;
      variable: Source<DataType.STRING>;
      argument: Source<DataType.STRING>;
    }
  | {
      operator: Operator.NumberGreaterThan;
      variable: Source<DataType.NUMBER>;
      argument: Source<DataType.NUMBER>;
    }
  | {
      operator: Operator.NumberLessThan;
      variable: Source<DataType.NUMBER>;
      argument: Source<DataType.NUMBER>;
    }
  | {
      operator: Operator.NumberGreaterThanOrEqualTo;
      variable: Source<DataType.NUMBER>;
      argument: Source<DataType.NUMBER>;
    }
  | {
      operator: Operator.NumberLessThanOrEqualTo;
      variable: Source<DataType.NUMBER>;
      argument: Source<DataType.NUMBER>;
    }
  | {
      operator: Operator.NumberEquals;
      variable: Source<DataType.NUMBER>;
      argument: Source<DataType.NUMBER>;
    }
  | {
      operator: Operator.NumberDoesNotEqual;
      variable: Source<DataType.NUMBER>;
      argument: Source<DataType.NUMBER>;
    }
  | {
      operator: Operator.DateTimeAfter;
      variable: Source<DataType.DATE>;
      argument: Source<DataType.DATE>;
    }
  | {
      operator: Operator.DateTimeBefore;
      variable: Source<DataType.DATE>;
      argument: Source<DataType.DATE>;
    }
  | {
      operator: Operator.DateTimeEquals;
      variable: Source<DataType.DATE>;
      argument: Source<DataType.DATE>;
    }
  | {
      operator: Operator.BooleanTrue;
      variable: Source<DataType.BOOLEAN>;
    }
  | {
      operator: Operator.BooleanFalse;
      variable: Source<DataType.BOOLEAN>;
    }
  | { operator: Operator.IsNull; variable: Source }
  | {
      operator: Operator.IsNotNull;
      variable: Source;
    }
  | { operator: Operator.Exists; variable: Source }
  | {
      operator: Operator.DoesNotExist;
      variable: Source;
    }
  | {
      operator: Operator.ArrayIsEmpty;
      variable: Source<DataType.ARRAY>;
    }
  | {
      operator: Operator.ArrayIsNotEmpty;
      variable: Source<DataType.ARRAY>;
    }
  | {
      operator: Operator.StringGreaterThan;
      variable: Source<DataType.STRING>;
      argument: Source<DataType.STRING>;
    }
  | {
      operator: Operator.StringLessThan;
      variable: Source<DataType.STRING>;
      argument: Source<DataType.STRING>;
    };

enum Operator {
  'None' = '$none',
  'StringContains' = '$stringContains',
  'StringDoesNotContain' = '$stringDoesNotContain',
  'StringExactlyMatches' = '$stringExactlyMatches',
  'StringDoesNotExactlyMatch' = '$stringDoesNotExactlyMatch',
  'StringIsIn' = '$stringIsIn',
  'StringIsNotIn' = '$stringIsNotIn',
  'StringStartsWith' = '$stringStartsWith',
  'StringDoesNotStartWith' = '$stringDoesNotStartWith',
  'StringEndsWith' = '$stringEndsWith',
  'StringDoesNotEndWith' = '$stringDoesNotEndWith',
  'NumberGreaterThan' = '$numberGreaterThan',
  'NumberLessThan' = '$numberLessThan',
  'NumberEquals' = '$numberEquals',
  'NumberDoesNotEqual' = '$numberDoesNotEqual',
  'NumberLessThanOrEqualTo' = '$numberLessThanOrEqualTo',
  'NumberGreaterThanOrEqualTo' = '$numberGreaterThanOrEqualTo',
  'DateTimeAfter' = '$dateTimeAfter',
  'DateTimeBefore' = '$dateTimeBefore',
  'DateTimeEquals' = '$dateTimeEquals',
  'BooleanTrue' = '$booleanTrue',
  'BooleanFalse' = '$booleanFalse',
  /** (PARA-5551) previously operators for Does Exist / Does Not Exist do a strict check for equality to null, resulting in a bug where value is undefined
   * to handle this situation, we are adding 2 new operators IsNotNull and IsNull and changing functionality of
   * Exist and DoesNotExist operators to handle undefined values
   */
  'IsNotNull' = '$exists',
  'IsNull' = '$doesNotExist',
  'Exists' = '$isNotUndefinedOrNull',
  'DoesNotExist' = '$isUndefinedOrNull',
  'ArrayIsIn' = '$arrayIsIn',
  'ArrayIsNotIn' = '$arrayIsNotIn',
  'ArrayIsEmpty' = '$arrayIsEmpty',
  'ArrayIsNotEmpty' = '$arrayIsNotEmpty',
  'StringGreaterThan' = '$stringGreaterThan',
  'StringLessThan' = '$stringLessThan',
}

type ExecutionSource = {
  type: 'EXECUTION';
  id: string; // step execution id (instance)
  stepId: string; // the base step id

  /**
   * a timestamp representing when the execution occurred
   */
  start: number;
  fanoutStack: FanoutStackEntry[];

  /**
   * This flag suggests that the content of the variable will be stored on remmote storage service
   * such as s3
   */
  remoteCached?: boolean;
};

// executions within fanouts are represented by a `FanoutExecutionSource`
// in redis, these are stored as a list of `ExecutionSource` types
// the `FanoutExecutionSource` is used to retrieve this list
// and a resolver determines which of the executions should be passed as variables
// depending on the context that the executing step is in
type FanoutExecutionSource = {
  type: 'FANOUT_EXECUTION';
  stepId: string; // step id of the execution, NOT the fanout
};

type UserSuppliedCredentialSource = {
  type: 'USER_SUPPLIED_CREDENTIAL';
  tokenType: TokenType;
  providerType: string;
  credential: TokenizedSource<DataType.STRING>;
};
